<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from "vue";
import type { TippyComponent } from "vue-tippy";
import { Tippy as VueTippy } from "vue-tippy";

import { onboardingGuideOpened } from "@/analytics/events/onboarding";
import { trackEvent } from "@/analytics/track";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { useModalStore } from "@/store/modal";
import { useOnboardingStore } from "@/store/onboarding";

const onboardingStore = useOnboardingStore();
const tippyRef = useTemplateRef<TippyComponent>("tippyRef");

let activeTimeout: ReturnType<typeof setTimeout> | undefined;

onboardingStore.registerMenuTooltipHandler(() => {
  clearTimeout(activeTimeout);
  tippyRef.value?.show();
  activeTimeout = setTimeout(() => tippyRef.value?.hide(), 5000);
});

onBeforeUnmount(() => {
  clearTimeout(activeTimeout);
  onboardingStore.registerMenuTooltipHandler(null);
});

function open() {
  tippyRef.value?.hide();
  trackEvent(onboardingGuideOpened());
  onboardingStore.markGuideOpened();
  useModalStore().open("OnboardingGuideModal");
}
</script>

<template>
  <VueTippy
    ref="tippyRef"
    placement="top"
    :delay="[300, 50]"
    :duration="150"
    :offset="[0, 17]"
    :popper-options="{
      modifiers: [{ name: 'preventOverflow', options: { altAxis: true, tether: false } }],
    }"
  >
    <BaseButton
      class="onboarding-menu-button"
      :class="{ pulsing: onboardingStore.shouldPulseMenuItem }"
      variant="outlined"
      color="grey"
      icon-before="general/learn"
      @click="open"
    >
      {{ $t("onboardingWelcome.continue") }}
    </BaseButton>
    <template #content>
      {{ $t("onboardingGuide.menuTooltip") }}
    </template>
  </VueTippy>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/shadow";

.onboarding-menu-button:hover {
  background-color: colors.$background-grey-light !important;
}

.onboarding-menu-button.pulsing {
  @include shadow.pulse-attention;
}
</style>
